import {
  IMPLICIT_BOT_BAN_CONFIRM_DELAY_MAX_MS,
  IMPLICIT_BOT_BAN_CONFIRM_DELAY_MIN_MS,
  IMPLICIT_BOT_BAN_DEADLINE_MARGIN_MS,
  IMPLICIT_BOT_BAN_FIRST_VOTE_DELAY_MAX_MS,
  IMPLICIT_BOT_BAN_FIRST_VOTE_DELAY_MIN_MS,
  IMPLICIT_BOT_BAN_NEXT_VOTE_DELAY_MAX_MS,
  IMPLICIT_BOT_BAN_NEXT_VOTE_DELAY_MIN_MS,
} from '../../configs/factionBans';

/**
 * Чистое ядро фазы банов рейтингового лобби (см. src/configs/factionBans.js).
 *
 * Голоса хранит сервер в виде
 *   votesByVoterId = {[голосующий]: {[цель]: factionId}}
 * и показывает их всем участникам лобби (но не в общем списке лобби). Свой
 * выбор голосующий подтверждает отдельно — confirmedVoterIds; фаза
 * закрывается досрочно, когда подтвердили все голосующие. Итог — по одной
 * забаненной фракции на игрока (или ни одной, если за него никто не голосовал).
 *
 * Здесь же живут правила, общие для сервера и клиента: кто голосует, что
 * считается допустимым голосом и какой пул фракций остается игроку после бана.
 */

/**
 * Голосует ли игрок в фазе банов. ИИ промо-матча фракцию не выбирает и не
 * банит (сервер решает за него сам), а боты со значком «бот» не банят никого:
 * честно помеченный бот не должен отнимать у людей фракции. Оба при этом
 * остаются целями — их фракции люди банят наравне с остальными.
 *
 * Неявные боты выдают себя за живых игроков и голосуют как люди (с паузами,
 * см. planImplicitBotBanTimeline).
 */
export const checkIfFactionBanVoter = (player) => Boolean(player) && !player.isAi && !player.isVisibleBot;

/** Id голосующих лобби — от них сервер ждет подтверждения банов */
export const getFactionBanVoterIds = (players = []) => players.filter(checkIfFactionBanVoter).map((player) => player.id);

/** Голос учитывается, только если цель — другой игрок, и фракция ей вообще выдана */
export const checkIfBanVoteAllowed = (voterId, targetPlayer, factionId) => {
  if (!voterId || !targetPlayer || voterId === targetPlayer.id) {
    return false;
  }
  return (targetPlayer.availableFactionIds || []).includes(factionId);
};

/**
 * Итог голосования: по фракции на игрока.
 *
 * Считаются только голоса игроков, которые все еще в лобби (ушедший уносит
 * свой голос с собой), и только за фракции из набора цели.
 *
 * Ничья разрешается порядком фракций в наборе САМОЙ ЦЕЛИ — детерминированно
 * и без случайности, которую пришлось бы прокидывать параметром. Набор и так
 * перемешан при раздаче (getRandomRankedFactions), так что предсказуемым для
 * игроков «всегда банится первая слева» это правило не делает.
 *
 * Игрок, за которого никто не голосовал, в результат не попадает вовсе —
 * ключа нет, бана нет.
 */
export const resolveFinalFactionBans = ({players = [], votesByVoterId = {}} = {}) => {
  const playerIds = new Set(players.map((player) => player.id));
  const bans = {};

  players.forEach((target) => {
    const availableFactionIds = target.availableFactionIds || [];
    if (availableFactionIds.length === 0) {
      return;
    }

    const votesByFactionId = {};
    Object.keys(votesByVoterId).forEach((voterId) => {
      if (!playerIds.has(voterId)) {
        return;
      }
      const factionId = (votesByVoterId[voterId] || {})[target.id];
      if (!checkIfBanVoteAllowed(voterId, target, factionId)) {
        return;
      }
      votesByFactionId[factionId] = (votesByFactionId[factionId] || 0) + 1;
    });

    // Перебор в порядке набора цели: первая из набравших максимум и побеждает
    let bannedFactionId = null;
    let maxVotes = 0;
    availableFactionIds.forEach((factionId) => {
      const votes = votesByFactionId[factionId] || 0;
      if (votes > maxVotes) {
        maxVotes = votes;
        bannedFactionId = factionId;
      }
    });

    if (bannedFactionId) {
      bans[target.id] = bannedFactionId;
    }
  });

  return bans;
};

/**
 * Все ли голосующие подтвердили свои баны — условие досрочного завершения фазы.
 *
 * voterIds задает сервер (getFactionBanVoterIds по текущему составу): ушедший
 * из лобби из ожидаемых выпадает, а оффлайн-игрок — нет, его фазу закроет
 * таймер. Пустой список голосующих фазу досрочно не закрывает.
 */
export const checkIfAllBanVotersConfirmed = ({voterIds = [], confirmedVoterIds = []} = {}) => {
  if (voterIds.length === 0) {
    return false;
  }
  const confirmedIds = new Set(confirmedVoterIds);
  return voterIds.every((voterId) => confirmedIds.has(voterId));
};

/**
 * Соперники, за которых голосующий еще не отдал допустимого голоса, — для
 * подсказки рядом с кнопкой подтверждения. Цели без выданных фракций не в счет.
 */
export const getMissingBanTargetIds = ({voterId, players = [], votes = {}} = {}) => players
  .filter((target) => target.id !== voterId && (target.availableFactionIds || []).length > 0)
  .filter((target) => !checkIfBanVoteAllowed(voterId, target, votes[target.id]))
  .map((target) => target.id);

/**
 * Кто проголосовал за бан фракции factionId у игрока targetPlayerId — в
 * порядке ключей votesByVoterId. Голос самому себе не голос.
 */
export const getBanVoterIdsForFaction = ({votesByVoterId = {}, targetPlayerId, factionId} = {}) => Object
  .keys(votesByVoterId)
  .filter((voterId) => voterId !== targetPlayerId && (votesByVoterId[voterId] || {})[targetPlayerId] === factionId);

/**
 * Голоса бота: по одной случайной фракции каждому ДРУГОМУ игроку.
 * roll вынесен параметром — тем же приемом, что и в resolveVisibleRankedBots,
 * чтобы поведение проверялось юнит-тестом без подмены Math.random.
 */
export const pickBotBanVotes = ({voterId, players = [], roll = Math.random} = {}) => {
  const votes = {};
  players.forEach((target) => {
    if (target.id === voterId) {
      return;
    }
    const pool = target.availableFactionIds || [];
    if (pool.length === 0) {
      return;
    }
    votes[target.id] = pool[Math.floor(roll() * pool.length)];
  });
  return votes;
};

// Случайная задержка в [min, max] — roll вынесен, как и в pickBotBanVotes
const getRolledDelayMs = (min, max, roll) => min + Math.floor(roll() * (max - min + 1));

/**
 * Расписание неявного бота в фазе банов: голоса по одному сопернику в
 * случайном порядке, с паузами, и подтверждение после последнего голоса.
 * Отметки — в мс от начала фазы.
 *
 * Если по сумме пауз подтверждение не успевает до phaseDurationMs минус
 * запас, все отметки сжимаются пропорционально: бот обязан подтвердить до
 * таймера, иначе фаза с ним никогда не закроется досрочно.
 *
 * @returns {{votes: Array<{targetId: string, atMs: number}>, confirmAtMs: number}}
 */
export const planImplicitBotBanTimeline = ({targetIds = [], phaseDurationMs, roll = Math.random} = {}) => {
  const order = [...targetIds];
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(roll() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }

  let atMs = getRolledDelayMs(IMPLICIT_BOT_BAN_FIRST_VOTE_DELAY_MIN_MS, IMPLICIT_BOT_BAN_FIRST_VOTE_DELAY_MAX_MS, roll);
  const votes = order.map((targetId, index) => {
    if (index > 0) {
      atMs += getRolledDelayMs(IMPLICIT_BOT_BAN_NEXT_VOTE_DELAY_MIN_MS, IMPLICIT_BOT_BAN_NEXT_VOTE_DELAY_MAX_MS, roll);
    }
    return {targetId, atMs};
  });
  let confirmAtMs = atMs + getRolledDelayMs(IMPLICIT_BOT_BAN_CONFIRM_DELAY_MIN_MS, IMPLICIT_BOT_BAN_CONFIRM_DELAY_MAX_MS, roll);

  const latestConfirmAtMs = phaseDurationMs - IMPLICIT_BOT_BAN_DEADLINE_MARGIN_MS;
  if (Number.isFinite(latestConfirmAtMs) && latestConfirmAtMs > 0 && confirmAtMs > latestConfirmAtMs) {
    const ratio = latestConfirmAtMs / confirmAtMs;
    votes.forEach((vote) => {
      vote.atMs = Math.floor(vote.atMs * ratio);
    });
    confirmAtMs = latestConfirmAtMs;
  }

  return {votes, confirmAtMs};
};

/**
 * Пул фракций игрока за вычетом забаненной — для ВСЕХ точек автоназначения
 * на сервере (истек таймер выбора, раскрытие «Случайной фракции», выбор
 * фракции ботом).
 *
 * Пустой остаток означал бы, что игроку вообще некого выбрать: тогда бан
 * игнорируется — лучше выдать забаненную фракцию, чем сорвать старт матча.
 */
export const getFactionPoolWithoutBan = (availableFactionIds = [], bannedFactionId = null) => {
  if (!bannedFactionId) {
    return availableFactionIds;
  }
  const pool = availableFactionIds.filter((factionId) => factionId !== bannedFactionId);
  return pool.length > 0 ? pool : availableFactionIds;
};

/**
 * Началась ли подготовка рейтингового лобби — фаза банов либо идущий за ней
 * выбор фракции. Признак держится до самого старта матча: после финализации
 * дедлайн фазы снимается, но isFactionBanPhaseResolved остается.
 */
export const checkIfRankedPhaseStarted = (lobby) => Boolean(
  lobby && lobby.isRanked && (lobby.factionBanDeadlineAt || lobby.isFactionBanPhaseResolved)
);

/**
 * Закрыт ли вход в рейтинговое лобби из-за уже начавшейся подготовки.
 *
 * Состав фиксируется на старте фазы банов, и пускать в него позже нельзя
 * сразу по нескольким причинам:
 *  - вошедшего никто не банил (итог считался без него), и он выбирал бы из
 *    четырех фракций против трех у остальных — прямое преимущество;
 *  - он становится новой целью голосования, а уже проголосовавшие боты
 *    второй раз не голосуют: досрочное завершение фазы больше недостижимо,
 *    и она всегда доедает таймер;
 *  - длительность фазы посчитана по прежнему составу и не продлевается;
 *  - в ручном лобби админа он ломает соответствие числа игроков выбранному
 *    полю, и старт падает с ошибкой уже после банов.
 *
 * Своих — тех, кто уже в составе, — проверка не касается: для них это
 * переподключение. Зрителей начатого матча тоже: их путь отдельный, а фазы к
 * тому времени позади.
 */
export const checkIfRankedPhaseJoinBlocked = (lobby, playerId, isSpectatorJoin = false) => {
  if (isSpectatorJoin || !checkIfRankedPhaseStarted(lobby)) {
    return false;
  }
  return !(lobby.players || []).some((player) => player.id === playerId);
};
